import KeyIcon from "@mui/icons-material/Key";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Button,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAccessToken, getRole, getUsername, hasSession, logoutSession } from "../auth/authSession";
import { markPasswordUpdated, rememberPasswordChangedAt, setRememberedUsername } from "../auth/loginPrefs";
import PasswordRules from "./ui/PasswordRules";
import { validatePasswordForm } from "../utils/passwordRules";

const UserProfile = ({ setOpen }) => {
  const decryptedRole = getRole();
  const navigate = useNavigate();
  const [userData, setUserData] = useState({});

  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [userImageUrl, setUserImageUrl] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    repeatPassword: "",
    mobile: "",
    designation: "",
    role: "",
    companyCodes: new Set(),
    isActive: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = getAccessToken();
    if (!hasSession()) {
          throw new Error("No auth token found. Please log in.");
        }

        const response = await fetch(`${apiUrl}/api/get-profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        const data = await response.json();

        if (data?.users) {
          setUserData(data.users);
        } else {
          throw new Error("User not found");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUser();
  }, [apiUrl]);

  useEffect(() => {
    if (!userData?.imagePath) return;

    const token = getAccessToken();
    if (!hasSession()) return;

    const fetchUserImage = async () => {
      try {
        const res = await fetch(
          `${apiUrl}${userData.imagePath}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) throw new Error("Failed to fetch user image");

        const blob = await res.blob();
        const imageUrl = URL.createObjectURL(blob);

        setUserImageUrl(imageUrl);
      } catch (err) {
        console.error("Image load error:", err);
      }
    };

    fetchUserImage();

    return () => {
      if (userImageUrl) URL.revokeObjectURL(userImageUrl);
    };
  }, [userData?.imagePath, apiUrl, userImageUrl]);


  const roleColorMap = {
    Sales_Person: "#ff5733",
    Sales_Coordinator: "#f39c12",
    Sales_Manager: "#3498db",
    Store_Manager: "#2ecc71",
    Office_Executive_L1: "#9b59b6",
    Office_Executive_L2: "#34495e",
    Office_Executive_L3: "#1abc9c",
    Accountant: "#3dd456",
    Security_Officer: "#3dd456",
    General_Manager: "#e74c3c",
    Super_Admin: "#8e44ad",
    ADMIN: "#d35400",
    Store_Keeper: "#2980b9",
    Authorization_Manager: "#16a085",
    Store_L1: "#ff5733",
    Store_L2: "#f39c12",
    Store_L3: "#3498db",
    Procurement_L1: "#2ecc71",
    Procurement_L2: "#9b59b6",
    Procurement_L3: "#34495e",
    Accounts_L1: "#1abc9c",
    Accounts_L2: "#3dd456",
    Accounts_L3: "#3dd456",
    IT_L1: "#e74c3c",
    IT_L2: "#8e44ad",
    IT_L3: "#d35400",
    Director: "#2980b9",
    Manager: "#16a085",
  };

  const getRoleColor = (role) => roleColorMap[role] || "#9E9E9E";

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validatePasswordForm({
      password: formData.password,
      repeatPassword: formData.repeatPassword,
      username: formData.username,
      roleName: decryptedRole,
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) return;

    try {
      const token = getAccessToken();
      const response = await fetch(
        `${apiUrl}/api/update-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            password: formData.password,
            repeatPassword: formData.repeatPassword,
          }),
        }
      );

      if (response.ok) {
        const signedInAs = formData.username || getUsername();
        setSuccessMessage("Password updated. Sign in with your new password.");
        setErrorMessage("");
        setRememberedUsername(signedInAs);
        markPasswordUpdated(signedInAs);
        rememberPasswordChangedAt();
        await logoutSession();
        setOpen(false);
        navigate("/login", {
          replace: true,
          state: { passwordUpdated: true, username: signedInAs },
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setErrorMessage(
          errorData.details || errorData.message || "Failed to update profile"
        );
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (error) {
      setErrorMessage(error.message || "Failed to update profile");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  useEffect(() => {
    if (!userData?.id) return;

    const fetchUserDetails = async () => {
      try {
        const token = getAccessToken();
    if (!hasSession()) throw new Error("No auth token found");

        const response = await fetch(`${apiUrl}/api/get-profile`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch user");

        const data = await response.json();
        const { users, companyCodes = [] } = data;

        if (!users) return;

        const { password, imagePath, repeatPassword, ...filteredUser } = users;

        setFormData({
          ...filteredUser,
          password: "",
          repeatPassword: "",
          companyCodes: new Set(companyCodes),
        });

        if (imagePath) {
          setProfileImage(`${apiUrl}${imagePath}`);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchUserDetails();
  }, [userData?.id, apiUrl]);

  return (
    <>
      <Box sx={{ padding: "20px", maxWidth: "100%", margin: "0 auto" }}>
        <Paper elevation={3} sx={{ padding: "24px" }}>
          <Typography
            variant="h6"
            component="h1"
            gutterBottom
            sx={{
              textAlign: "center",
              color: "black",
              marginBottom: "3px",
            }}
          >
            BASIC INFO
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { sm: "column", lg: "row" }, // Image moves down on small screens
              gap: 3,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Image section */}
            <Box
              sx={{
                width: "400px",
                height: "400px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {userData?.imagePath ? (
                <img
                  src={userImageUrl}
                  alt={`${userData.firstName} ${userData.lastName}`}
                  style={{
                    maxWidth: "400px",
                    maxHeight: "400px",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Box sx={{ p: 2, textAlign: "center", color: "#666" }}>
                  No image available
                </Box>
              )}
            </Box>

            {/* Product info section */}
            <Box sx={{ flex: 1 }}>
              <Grid container spacing={0}>
                {[
                  {
                    label: "NAME",
                    value: `${userData?.firstName ?? ""} ${userData?.lastName ?? ""
                      }`,
                  },
                  {
                    label: "EMAIL",
                    value: userData?.email ?? "-",
                  },
                  {
                    label: "MOBILE",
                    value: userData?.mobile ?? "-",
                  },
                  {
                    label: "DESIGNATION",
                    value: userData?.designation ?? "-",
                  },
                ].map((field, index, array) => {
                  const isRoleField = field.label === "USER LEVEL";
                  const roleColor = getRoleColor(userData.role);

                  return (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          borderTop: "1px solid #e0e0e0",
                          borderLeft: "1px solid #e0e0e0",
                          borderRight: "1px solid #e0e0e0",
                          borderBottom:
                            index === array.length - 1
                              ? "1px solid #e0e0e0"
                              : "none",
                          padding: "0px 0",
                        }}
                      >
                        <FormLabel
                          component="legend"
                          sx={{
                            width: "200px",
                            fontWeight: "bold",
                            color: "#666",
                            marginLeft: "10px",
                            borderRight: "1px solid #e0e0e0",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {field.label}
                        </FormLabel>

                        <TextField
                          variant="outlined"
                          value={field.value}
                          sx={{
                            flex: 1,
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            "& .MuiInputBase-input": isRoleField
                              ? {
                                color: "#fff",
                                backgroundColor: roleColor,
                                borderRadius: "8px",
                                padding: "2px 0px",
                                fontWeight: "bold",
                                textTransform: "capitalize",
                                textAlign: "center",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }
                              : {},
                          }}
                          InputProps={{
                            readOnly: true,
                            style: {
                              height: "40px",
                              backgroundColor: "transparent",
                              fontSize: "1rem",
                              padding: "0 12px",
                              textAlign: "center",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Box>
        </Paper>
      </Box>
      <Box sx={{ padding: "20px", maxWidth: "100%", margin: "0 auto" }}>
        <Paper elevation={3} sx={{ padding: "24px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
              gap: 1,
            }}
          >
            <LockOutlinedIcon
              sx={{ color: "secondary.main", fontSize: { xs: 28, sm: 32 } }}
            />
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontWeight: 700, textAlign: "center" }}
            >
              Change password
            </Typography>
            <KeyIcon sx={{ color: "primary.main", fontSize: { xs: 28, sm: 32 } }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Signed in as <strong>{formData.username || "your account"}</strong>
          </Typography>
          <PasswordRules
            password={formData.password}
            username={formData.username}
            role={decryptedRole}
            confirm={formData.repeatPassword}
            showPolicy
          />
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="New password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  error={!!errors.password}
                  helperText={errors.password}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword((open) => !open)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirm password"
                  type={showRepeatPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.repeatPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      repeatPassword: e.target.value,
                    })
                  }
                  error={!!errors.repeatPassword}
                  helperText={errors.repeatPassword}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showRepeatPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowRepeatPassword((open) => !open)}
                          edge="end"
                          size="small"
                        >
                          {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                {successMessage ? (
                  <Alert severity="success">{successMessage}</Alert>
                ) : null}
                {errorMessage ? (
                  <Alert severity="error">{errorMessage}</Alert>
                ) : null}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                >
                  Update password
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
};

export default UserProfile;
